/** @jsxImportSource preact */
/**
 * The harm severity scale: 13 harm categories down the side, the five
 * levels (Negligible .. Catastrophic) across the top, and in each cell what
 * a rating at that level means for that category, with how many incidents
 * carry that direct-harm rating along the cell's bottom edge. Replaces the
 * embedded Google Sheet on airisk.mit.edu/ai-incident-tracker/harm-taxonomy.
 *
 * A cell opens SeverityCellModal: the composition of those incidents and
 * their list. Direct-harm ratings only on this page.
 */
import { h } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { IMPACT_LABELS, IMPACT_PALETTE, withAlpha } from '@/data/impactScale';
import { HARM_SCALE_CATEGORIES, HARM_SEVERITY_DEFINITIONS } from '@/data/harmSeverityScale';
import { loadTheme } from '@/lib/theme';
import type { SeverityMatrix } from '@/lib/severityMatrix';
import { SeverityCellModal, SeverityCellRef } from './SeverityCellModal';

interface HarmSeverityScaleProps {
  apiBase: string;
  containerId: string;
  /** Incidents dataset (API `src`); the default dataset rates only the original ten categories. */
  dataset?: string;
}

const LEVELS = [1, 2, 3, 4, 5] as const;
const LEVEL_NAMES = IMPACT_LABELS.standard;
/** Text colour that reads on each level's swatch. */
const LEVEL_INK: Record<number, string> = { 1: '#111827', 2: '#111827', 3: '#111827', 4: '#ffffff', 5: '#ffffff' };
const CSET_URL = 'https://cset.georgetown.edu/publication/cset-ai-harm-taxonomy-for-aiid-and-management-guidance/';

const STYLE_ID = 'gc-harm-scale-styles';
function injectScaleStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const ui = loadTheme().colors.ui;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .gc-hs { color: ${ui.text}; font-size: 14px; }
    .gc-hs-scroll { overflow-x: auto; border: 1px solid ${ui.border}; border-radius: 8px; background: ${ui.background}; }
    .gc-hs table { border-collapse: separate; border-spacing: 0; width: 100%; min-width: 900px; table-layout: fixed; }
    .gc-hs th, .gc-hs td { vertical-align: top; text-align: left; }
    .gc-hs thead th { position: sticky; top: 0; z-index: 2; padding: 10px 12px; font-weight: 700; font-size: 14px; }
    .gc-hs thead th.gc-hs-corner { left: 0; z-index: 3; background: ${ui.background}; color: ${ui.textMuted};
      font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; border-bottom: 1px solid ${ui.border}; }
    .gc-hs th.gc-hs-cat { position: sticky; left: 0; z-index: 1; background: ${ui.background}; padding: 12px;
      font-weight: 700; font-size: 14px; border-right: 1px solid ${ui.border}; border-bottom: 1px solid ${ui.border}; }
    .gc-hs td.gc-hs-cell { position: relative; padding: 12px 12px 36px; border-left: 1px solid ${ui.border}; border-bottom: 1px solid ${ui.border};
      font-size: 13px; line-height: 1.45; background: ${ui.background}; cursor: pointer; }
    .gc-hs-plain td.gc-hs-cell { padding-bottom: 12px; }
    .gc-hs td.gc-hs-cell::before { content: ""; position: absolute; left: 0; right: 0; top: 0; height: 3px; background: var(--gc-hs-level); }
    .gc-hs td.gc-hs-cell:hover, .gc-hs td.gc-hs-cell:focus-visible { outline: 2px solid ${ui.text}; outline-offset: -2px; }
    .gc-hs-count { position: absolute; left: 12px; right: 12px; bottom: 10px; font-size: 12px; color: ${ui.textMuted};
      font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .gc-hs-count b { color: ${ui.text}; font-weight: 600; }
    @media (prefers-reduced-motion: no-preference) { .gc-hs td.gc-hs-cell { transition: background-color .15s; } }
    .gc-hs col.gc-hs-c0 { width: 200px; }
    .gc-hs-swipe { display: none; margin: 8px 0 0; font-size: 12px; color: ${ui.textMuted}; }
    /* Phones: the category column no longer sticks (frozen at 200px it
       left half the screen for the five levels) and narrows, its names
       wrapping; each level column keeps a readable width and the table
       scrolls sideways, which the hint below it says. */
    @media (max-width: 719px) {
      .gc-hs table { min-width: 1010px; }
      .gc-hs col.gc-hs-c0 { width: 110px; }
      .gc-hs th.gc-hs-cat { position: static; font-size: 12.5px; padding: 10px 8px; overflow-wrap: anywhere; }
      .gc-hs thead th.gc-hs-corner { left: auto; }
      .gc-hs td.gc-hs-cell { padding: 10px 10px 34px; font-size: 12.5px; }
      .gc-hs-plain td.gc-hs-cell { padding-bottom: 10px; }
      .gc-hs-count { left: 10px; right: 10px; }
      .gc-hs-swipe { display: block; }
    }
  `;
  document.head.appendChild(style);
}

const levelColor = (level: number) => IMPACT_PALETTE[level - 1].bg;
const fmt = (n: number) => n.toLocaleString();

export function HarmSeverityScale({ apiBase, containerId, dataset }: HarmSeverityScaleProps) {
  const ui = loadTheme().colors.ui;
  const [matrix, setMatrix] = useState<SeverityMatrix | null>(null);
  const [totalIncidents, setTotalIncidents] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [cell, setCell] = useState<SeverityCellRef | null>(null);
  // The scale alone, for reading the definitions without the counts.
  const [showCounts, setShowCounts] = useState(true);

  useEffect(() => { injectScaleStyles(); }, []);

  // The counts are fetched once, right after the scale has painted. A
  // failed fetch leaves the scale readable and offers a Retry.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    const qs = new URLSearchParams();
    if (dataset) qs.set('src', dataset);
    qs.set('chartType', 'severity-matrix');
    const timer = setTimeout(() => {
      fetch(`${apiBase}/data/incidents?${qs}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`API error: ${r.status}`))))
        .then((json: SeverityMatrix & { totalIncidents?: number }) => {
          if (cancelled) return;
          setMatrix(json);
          if (typeof json.totalIncidents === 'number') setTotalIncidents(json.totalIncidents);
        })
        .catch((err: Error) => { if (!cancelled) setError(err.message || 'Could not load incident counts'); });
    }, 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [apiBase, dataset, fetchAttempt]);

  const byKey = useMemo(() => new Map((matrix?.categories ?? []).map((c) => [c.key, c])), [matrix]);
  const maxCount = useMemo(() => {
    let max = 0;
    for (const { key } of HARM_SCALE_CATEGORIES) for (const c of byKey.get(key)?.direct ?? []) max = Math.max(max, c.total);
    return max;
  }, [byKey]);
  const cellCount = (key: string, level: number): number | null => byKey.get(key)?.direct[level - 1].total ?? null;
  // A faint wash in the level's own colour, square-root scaled so a cell with
  // a few incidents still shows against the thousand-strong Negligible cells.
  const cellTint = (level: number, count: number | null): string | undefined =>
    showCounts && count && maxCount > 0 ? withAlpha(levelColor(level), 0.05 + 0.22 * Math.sqrt(count / maxCount)) : undefined;

  const open = (key: string, label: string, level: number) => setCell({ key, label, level });

  return (
    <div class="gc-hs" id={`${containerId}-scale`}>
      {error && (
        <p style={{ margin: '0 0 10px', fontSize: 13, color: ui.error }}>
          {error}: the scale is shown without incident counts.{' '}
          <button type="button" onClick={() => setFetchAttempt((n) => n + 1)}
            style={{ font: 'inherit', fontSize: 12, border: `1px solid ${ui.border}`, borderRadius: 6, padding: '2px 8px', background: ui.background, color: ui.text, cursor: 'pointer' }}>
            Retry
          </button>
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button
          type="button"
          id={`${containerId}-toggle-counts`}
          aria-pressed={showCounts}
          onClick={() => setShowCounts((v) => !v)}
          style={{ font: 'inherit', fontSize: 13, border: `1px solid ${ui.border}`, borderRadius: 6, padding: '5px 12px', background: ui.background, color: ui.text, cursor: 'pointer' }}
        >
          {showCounts ? 'Hide incident counts' : 'Show incident counts'}
        </button>
      </div>

      <div class={`gc-hs-scroll${showCounts ? '' : ' gc-hs-plain'}`}>
        <table>
          <colgroup>
            <col class="gc-hs-c0" />
            {LEVELS.map((l) => <col key={l} />)}
          </colgroup>
          <thead>
            <tr>
              <th scope="col" class="gc-hs-corner">Harm category</th>
              {LEVELS.map((level) => (
                <th key={level} scope="col" style={{ background: levelColor(level), color: LEVEL_INK[level] }}>
                  {LEVEL_NAMES[level]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HARM_SCALE_CATEGORIES.map(({ key, label }) => (
              <tr key={key}>
                <th scope="row" class="gc-hs-cat">{label}</th>
                {LEVELS.map((level) => {
                  const count = cellCount(key, level);
                  const countText = showCounts && count != null && totalIncidents != null ? `${fmt(count)} of ${fmt(totalIncidents)} incidents` : '';
                  return (
                    <td
                      key={level}
                      class="gc-hs-cell"
                      style={{ '--gc-hs-level': levelColor(level), background: cellTint(level, count) } as Record<string, string | undefined>}
                      role="button"
                      tabIndex={0}
                      aria-label={`${label}, ${LEVEL_NAMES[level]}${countText ? `: ${countText}` : ''}. Open the incidents.`}
                      onClick={() => open(key, label, level)}
                      onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(key, label, level); } }}
                    >
                      {HARM_SEVERITY_DEFINITIONS[key]?.[level - 1]}
                      {showCounts && (
                        <span class="gc-hs-count" aria-hidden="true">
                          {count != null && totalIncidents != null ? <span><b>{fmt(count)}</b> of {fmt(totalIncidents)} incidents</span> : null}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p class="gc-hs-swipe">Swipe sideways to see all five levels.</p>

      <p style={{ fontSize: 12.5, color: ui.textMuted, margin: '10px 0 0', maxWidth: '95ch', lineHeight: 1.5 }}>
        Counts are the incidents assigned that rating for direct harm in that category; select a cell to see how they break down and which they are.
        Categories are based on the{' '}
        <a href={CSET_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gc-link, #a32035)' }}>CSET Taxonomy of AI Harm</a>.
        The CSET &lsquo;Additional Definitions&rsquo; and &lsquo;Other&rsquo; categories are not used, and &lsquo;Detrimental Content&rsquo; is
        named &lsquo;Toxic or Malicious Content&rsquo; here. Psychological Harm, Epistemic Harm and Child Sexual Exploitation and Abuse
        extend the CSET categories and are rated on the same scale.
      </p>

      {cell && (
        <SeverityCellModal
          key={`${cell.key}-${cell.level}`}
          apiBase={apiBase}
          dataset={dataset}
          cell={cell}
          totalIncidents={totalIncidents}
          onClose={() => setCell(null)}
        />
      )}
    </div>
  );
}
